import { f as featureFlagStore, L as Logger, g as getSpaceName, a as getEnvironment, P as PendoApiKey, c as getLanguage, B as BuiltInGroups, r as removeObjectUndefinedProperties, F as FeatureFlags } from './featureFlags-0dc96f8f.js';

/**
 * Copyright 2016 Google Inc. All Rights Reserved.
 *
 * Licensed under the W3C SOFTWARE AND DOCUMENT NOTICE AND LICENSE.
 *
 *  https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
 *
 */
(function() {

// Exit early if we're not running in a browser.
if (typeof window !== 'object') {
  return;
}

// Exit early if all IntersectionObserver and IntersectionObserverEntry
// features are natively supported.
if ('IntersectionObserver' in window &&
    'IntersectionObserverEntry' in window &&
    'intersectionRatio' in window.IntersectionObserverEntry.prototype) {

  // Minimal polyfill for Edge 15's lack of `isIntersecting`
  // See: https://github.com/w3c/IntersectionObserver/issues/211
  if (!('isIntersecting' in window.IntersectionObserverEntry.prototype)) {
    Object.defineProperty(window.IntersectionObserverEntry.prototype,
      'isIntersecting', {
      get: function () {
        return this.intersectionRatio > 0;
      }
    });
  }
  return;
}

/**
 * Returns the embedding frame element, if any.
 * @param {!Document} doc
 * @return {!Element}
 */
function getFrameElement(doc) {
  try {
    return doc.defaultView && doc.defaultView.frameElement || null;
  } catch (e) {
    // Ignore the error.
    return null;
  }
}

/**
 * A local reference to the root document.
 */
var document = (function(startDoc) {
  var doc = startDoc;
  var frame = getFrameElement(doc);
  while (frame) {
    doc = frame.ownerDocument;
    frame = getFrameElement(doc);
  }
  return doc;
})(window.document);

/**
 * An IntersectionObserver registry. This registry exists to hold a strong
 * reference to IntersectionObserver instances currently observing a target
 * element. Without this registry, instances without another reference may be
 * garbage collected.
 */
var registry = [];

/**
 * The signal updater for cross-origin intersection. When not null, it means
 * that the polyfill is configured to work in a cross-origin mode.
 * @type {function(DOMRect|ClientRect, DOMRect|ClientRect)}
 */
var crossOriginUpdater = null;

/**
 * The current cross-origin intersection. Only used in the cross-origin mode.
 * @type {DOMRect|ClientRect}
 */
var crossOriginRect = null;


/**
 * Creates the global IntersectionObserverEntry constructor.
 * https://w3c.github.io/IntersectionObserver/#intersection-observer-entry
 * @param {Object} entry A dictionary of instance properties.
 * @constructor
 */
function IntersectionObserverEntry(entry) {
  this.time = entry.time;
  this.target = entry.target;
  this.rootBounds = ensureDOMRect(entry.rootBounds);
  this.boundingClientRect = ensureDOMRect(entry.boundingClientRect);
  this.intersectionRect = ensureDOMRect(entry.intersectionRect || getEmptyRect());
  this.isIntersecting = !!entry.intersectionRect;

  // Calculates the intersection ratio.
  var targetRect = this.boundingClientRect;
  var targetArea = targetRect.width * targetRect.height;
  var intersectionRect = this.intersectionRect;
  var intersectionArea = intersectionRect.width * intersectionRect.height;

  // Sets intersection ratio.
  if (targetArea) {
    // Round the intersection ratio to avoid floating point math issues:
    // https://github.com/w3c/IntersectionObserver/issues/324
    this.intersectionRatio = Number((intersectionArea / targetArea).toFixed(4));
  } else {
    // If area is zero and is intersecting, sets to 1, otherwise to 0
    this.intersectionRatio = this.isIntersecting ? 1 : 0;
  }
}


/**
 * Creates the global IntersectionObserver constructor.
 * https://w3c.github.io/IntersectionObserver/#intersection-observer-interface
 * @param {Function} callback The function to be invoked after intersection
 *     changes have queued. The function is not invoked if the queue has
 *     been emptied by calling the `takeRecords` method.
 * @param {Object=} opt_options Optional configuration options.
 * @constructor
 */
function IntersectionObserver(callback, opt_options) {

  var options = opt_options || {};

  if (typeof callback != 'function') {
    throw new Error('callback must be a function');
  }

  if (
    options.root &&
    options.root.nodeType != 1 &&
    options.root.nodeType != 9
  ) {
    throw new Error('root must be a Document or Element');
  }

  // Binds and throttles `this._checkForIntersections`.
  this._checkForIntersections = throttle(
      this._checkForIntersections.bind(this), this.THROTTLE_TIMEOUT);

  // Private properties.
  this._callback = callback;
  this._observationTargets = [];
  this._queuedEntries = [];
  this._rootMarginValues = this._parseRootMargin(options.rootMargin);

  // Public properties.
  this.thresholds = this._initThresholds(options.threshold);
  this.root = options.root || null;
  this.rootMargin = this._rootMarginValues.map(function(margin) {
    return margin.value + margin.unit;
  }).join(' ');

  /** @private @const {!Array<!Document>} */
  this._monitoringDocuments = [];
  /** @private @const {!Array<function()>} */
  this._monitoringUnsubscribes = [];
}


/**
 * The minimum interval within which the document will be checked for
 * intersection changes.
 */
IntersectionObserver.prototype.THROTTLE_TIMEOUT = 100;


/**
 * The frequency in which the polyfill polls for intersection changes.
 * this can be updated on a per instance basis and must be set prior to
 * calling `observe` on the first target.
 */
IntersectionObserver.prototype.POLL_INTERVAL = null;

/**
 * Use a mutation observer on the root element
 * to detect intersection changes.
 */
IntersectionObserver.prototype.USE_MUTATION_OBSERVER = true;


/**
 * Sets up the polyfill in the cross-origin mode. The result is the
 * updater function that accepts two arguments: `boundingClientRect` and
 * `intersectionRect` - just as these fields would be available to the
 * parent via `IntersectionObserverEntry`. This function should be called
 * each time the iframe receives intersection information from the parent
 * window, e.g. via messaging.
 * @return {function(DOMRect|ClientRect, DOMRect|ClientRect)}
 */
IntersectionObserver._setupCrossOriginUpdater = function() {
  if (!crossOriginUpdater) {
    /**
     * @param {DOMRect|ClientRect} boundingClientRect
     * @param {DOMRect|ClientRect} intersectionRect
     */
    crossOriginUpdater = function(boundingClientRect, intersectionRect) {
      if (!boundingClientRect || !intersectionRect) {
        crossOriginRect = getEmptyRect();
      } else {
        crossOriginRect = convertFromParentRect(boundingClientRect, intersectionRect);
      }
      registry.forEach(function(observer) {
        observer._checkForIntersections();
      });
    };
  }
  return crossOriginUpdater;
};


/**
 * Resets the cross-origin mode.
 */
IntersectionObserver._resetCrossOriginUpdater = function() {
  crossOriginUpdater = null;
  crossOriginRect = null;
};


/**
 * Starts observing a target element for intersection changes based on
 * the thresholds values.
 * @param {Element} target The DOM element to observe.
 */
IntersectionObserver.prototype.observe = function(target) {
  var isTargetAlreadyObserved = this._observationTargets.some(function(item) {
    return item.element == target;
  });

  if (isTargetAlreadyObserved) {
    return;
  }

  if (!(target && target.nodeType == 1)) {
    throw new Error('target must be an Element');
  }

  this._registerInstance();
  this._observationTargets.push({element: target, entry: null});
  this._monitorIntersections(target.ownerDocument);
  this._checkForIntersections();
};


/**
 * Stops observing a target element for intersection changes.
 * @param {Element} target The DOM element to observe.
 */
IntersectionObserver.prototype.unobserve = function(target) {
  this._observationTargets =
      this._observationTargets.filter(function(item) {
        return item.element != target;
      });
  this._unmonitorIntersections(target.ownerDocument);
  if (this._observationTargets.length == 0) {
    this._unregisterInstance();
  }
};


/**
 * Stops observing all target elements for intersection changes.
 */
IntersectionObserver.prototype.disconnect = function() {
  this._observationTargets = [];
  this._unmonitorAllIntersections();
  this._unregisterInstance();
};


/**
 * Returns any queue entries that have not yet been reported to the
 * callback and clears the queue. This can be used in conjunction with the
 * callback to obtain the absolute most up-to-date intersection information.
 * @return {Array} The currently queued entries.
 */
IntersectionObserver.prototype.takeRecords = function() {
  var records = this._queuedEntries.slice();
  this._queuedEntries = [];
  return records;
};


/**
 * Accepts the threshold value from the user configuration object and
 * returns a sorted array of unique threshold values. If a value is not
 * between 0 and 1 and error is thrown.
 * @private
 * @param {Array|number=} opt_threshold An optional threshold value or
 *     a list of threshold values, defaulting to [0].
 * @return {Array} A sorted list of unique and valid threshold values.
 */
IntersectionObserver.prototype._initThresholds = function(opt_threshold) {
  var threshold = opt_threshold || [0];
  if (!Array.isArray(threshold)) threshold = [threshold];

  return threshold.sort().filter(function(t, i, a) {
    if (typeof t != 'number' || isNaN(t) || t < 0 || t > 1) {
      throw new Error('threshold must be a number between 0 and 1 inclusively');
    }
    return t !== a[i - 1];
  });
};


/**
 * Accepts the rootMargin value from the user configuration object
 * and returns an array of the four margin values as an object containing
 * the value and unit properties. If any of the values are not properly
 * formatted or use a unit other than px or %, and error is thrown.
 * @private
 * @param {string=} opt_rootMargin An optional rootMargin value,
 *     defaulting to '0px'.
 * @return {Array<Object>} An array of margin objects with the keys
 *     value and unit.
 */
IntersectionObserver.prototype._parseRootMargin = function(opt_rootMargin) {
  var marginString = opt_rootMargin || '0px';
  var margins = marginString.split(/\s+/).map(function(margin) {
    var parts = /^(-?\d*\.?\d+)(px|%)$/.exec(margin);
    if (!parts) {
      throw new Error('rootMargin must be specified in pixels or percent');
    }
    return {value: parseFloat(parts[1]), unit: parts[2]};
  });

  // Handles shorthand.
  margins[1] = margins[1] || margins[0];
  margins[2] = margins[2] || margins[0];
  margins[3] = margins[3] || margins[1];

  return margins;
};


/**
 * Starts polling for intersection changes if the polling is not already
 * happening, and if the page's visibility state is visible.
 * @param {!Document} doc
 * @private
 */
IntersectionObserver.prototype._monitorIntersections = function(doc) {
  var win = doc.defaultView;
  if (!win) {
    // Already destroyed.
    return;
  }
  if (this._monitoringDocuments.indexOf(doc) != -1) {
    // Already monitoring.
    return;
  }

  // Private state for monitoring.
  var callback = this._checkForIntersections;
  var monitoringInterval = null;
  var domObserver = null;

  // If a poll interval is set, use polling instead of listening to
  // resize and scroll events or DOM mutations.
  if (this.POLL_INTERVAL) {
    monitoringInterval = win.setInterval(callback, this.POLL_INTERVAL);
  } else {
    addEvent(win, 'resize', callback, true);
    addEvent(doc, 'scroll', callback, true);
    if (this.USE_MUTATION_OBSERVER && 'MutationObserver' in win) {
      domObserver = new win.MutationObserver(callback);
      domObserver.observe(doc, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true
      });
    }
  }

  this._monitoringDocuments.push(doc);
  this._monitoringUnsubscribes.push(function() {
    // Get the window object again. When a friendly iframe is destroyed, it
    // will be null.
    var win = doc.defaultView;

    if (win) {
      if (monitoringInterval) {
        win.clearInterval(monitoringInterval);
      }
      removeEvent(win, 'resize', callback, true);
    }

    removeEvent(doc, 'scroll', callback, true);
    if (domObserver) {
      domObserver.disconnect();
    }
  });

  // Also monitor the parent.
  var rootDoc =
    (this.root && (this.root.ownerDocument || this.root)) || document;
  if (doc != rootDoc) {
    var frame = getFrameElement(doc);
    if (frame) {
      this._monitorIntersections(frame.ownerDocument);
    }
  }
};


/**
 * Stops polling for intersection changes.
 * @param {!Document} doc
 * @private
 */
IntersectionObserver.prototype._unmonitorIntersections = function(doc) {
  var index = this._monitoringDocuments.indexOf(doc);
  if (index == -1) {
    return;
  }

  var rootDoc =
    (this.root && (this.root.ownerDocument || this.root)) || document;

  // Check if any dependent targets are still remaining.
  var hasDependentTargets =
      this._observationTargets.some(function(item) {
        var itemDoc = item.element.ownerDocument;
        // Target is in this context.
        if (itemDoc == doc) {
          return true;
        }
        // Target is nested in this context.
        while (itemDoc && itemDoc != rootDoc) {
          var frame = getFrameElement(itemDoc);
          itemDoc = frame && frame.ownerDocument;
          if (itemDoc == doc) {
            return true;
          }
        }
        return false;
      });
  if (hasDependentTargets) {
    return;
  }

  // Unsubscribe.
  var unsubscribe = this._monitoringUnsubscribes[index];
  this._monitoringDocuments.splice(index, 1);
  this._monitoringUnsubscribes.splice(index, 1);
  unsubscribe();

  // Also unmonitor the parent.
  if (doc != rootDoc) {
    var frame = getFrameElement(doc);
    if (frame) {
      this._unmonitorIntersections(frame.ownerDocument);
    }
  }
};


/**
 * Stops polling for intersection changes.
 * @param {!Document} doc
 * @private
 */
IntersectionObserver.prototype._unmonitorAllIntersections = function() {
  var unsubscribes = this._monitoringUnsubscribes.slice(0);
  this._monitoringDocuments.length = 0;
  this._monitoringUnsubscribes.length = 0;
  for (var i = 0; i < unsubscribes.length; i++) {
    unsubscribes[i]();
  }
};


/**
 * Scans each observation target for intersection changes and adds them
 * to the internal entries queue. If new entries are found, it
 * schedules the callback to be invoked.
 * @private
 */
IntersectionObserver.prototype._checkForIntersections = function() {
  if (!this.root && crossOriginUpdater && !crossOriginRect) {
    // Cross origin monitoring, but no initial data available yet.
    return;
  }

  var rootIsInDom = this._rootIsInDom();
  var rootRect = rootIsInDom ? this._getRootRect() : getEmptyRect();

  this._observationTargets.forEach(function(item) {
    var target = item.element;
    var targetRect = getBoundingClientRect(target);
    var rootContainsTarget = this._rootContainsTarget(target);
    var oldEntry = item.entry;
    var intersectionRect = rootIsInDom && rootContainsTarget &&
        this._computeTargetAndRootIntersection(target, targetRect, rootRect);

    var rootBounds = null;
    if (!this._rootContainsTarget(target)) {
      rootBounds = getEmptyRect();
    } else if (!crossOriginUpdater || this.root) {
      rootBounds = rootRect;
    }

    var newEntry = item.entry = new IntersectionObserverEntry({
      time: now(),
      target: target,
      boundingClientRect: targetRect,
      rootBounds: rootBounds,
      intersectionRect: intersectionRect
    });

    if (!oldEntry) {
      this._queuedEntries.push(newEntry);
    } else if (rootIsInDom && rootContainsTarget) {
      // If the new entry intersection ratio has crossed any of the
      // thresholds, add a new entry.
      if (this._hasCrossedThreshold(oldEntry, newEntry)) {
        this._queuedEntries.push(newEntry);
      }
    } else {
      // If the root is not in the DOM or target is not contained within
      // root but the previous entry for this target had an intersection,
      // add a new record indicating removal.
      if (oldEntry && oldEntry.isIntersecting) {
        this._queuedEntries.push(newEntry);
      }
    }
  }, this);

  if (this._queuedEntries.length) {
    this._callback(this.takeRecords(), this);
  }
};


/**
 * Accepts a target and root rect computes the intersection between then
 * following the algorithm in the spec.
 * TODO(philipwalton): at this time clip-path is not considered.
 * https://w3c.github.io/IntersectionObserver/#calculate-intersection-rect-algo
 * @param {Element} target The target DOM element
 * @param {Object} targetRect The bounding rect of the target.
 * @param {Object} rootRect The bounding rect of the root after being
 *     expanded by the rootMargin value.
 * @return {?Object} The final intersection rect object or undefined if no
 *     intersection is found.
 * @private
 */
IntersectionObserver.prototype._computeTargetAndRootIntersection =
    function(target, targetRect, rootRect) {
  // If the element isn't displayed, an intersection can't happen.
  if (window.getComputedStyle(target).display == 'none') return;

  var intersectionRect = targetRect;
  var parent = getParentNode(target);
  var atRoot = false;

  while (!atRoot && parent) {
    var parentRect = null;
    var parentComputedStyle = parent.nodeType == 1 ?
        window.getComputedStyle(parent) : {};

    // If the parent isn't displayed, an intersection can't happen.
    if (parentComputedStyle.display == 'none') return null;

    if (parent == this.root || parent.nodeType == /* DOCUMENT */ 9) {
      atRoot = true;
      if (parent == this.root || parent == document) {
        if (crossOriginUpdater && !this.root) {
          if (!crossOriginRect ||
              crossOriginRect.width == 0 && crossOriginRect.height == 0) {
            // A 0-size cross-origin intersection means no-intersection.
            parent = null;
            parentRect = null;
            intersectionRect = null;
          } else {
            parentRect = crossOriginRect;
          }
        } else {
          parentRect = rootRect;
        }
      } else {
        // Check if there's a frame that can be navigated to.
        var frame = getParentNode(parent);
        var frameRect = frame && getBoundingClientRect(frame);
        var frameIntersect =
            frame &&
            this._computeTargetAndRootIntersection(frame, frameRect, rootRect);
        if (frameRect && frameIntersect) {
          parent = frame;
          parentRect = convertFromParentRect(frameRect, frameIntersect);
        } else {
          parent = null;
          intersectionRect = null;
        }
      }
    } else {
      // If the element has a non-visible overflow, and it's not the <body>
      // or <html> element, update the intersection rect.
      // Note: <body> and <html> cannot be clipped to a rect that's not also
      // the document rect, so no need to compute a new intersection.
      var doc = parent.ownerDocument;
      if (parent != doc.body &&
          parent != doc.documentElement &&
          parentComputedStyle.overflow != 'visible') {
        parentRect = getBoundingClientRect(parent);
      }
    }

    // If either of the above conditionals set a new parentRect,
    // calculate new intersection data.
    if (parentRect) {
      intersectionRect = computeRectIntersection(parentRect, intersectionRect);
    }
    if (!intersectionRect) break;
    parent = parent && getParentNode(parent);
  }
  return intersectionRect;
};


/**
 * Returns the root rect after being expanded by the rootMargin value.
 * @return {ClientRect} The expanded root rect.
 * @private
 */
IntersectionObserver.prototype._getRootRect = function() {
  var rootRect;
  if (this.root && !isDoc(this.root)) {
    rootRect = getBoundingClientRect(this.root);
  } else {
    // Use <html>/<body> instead of window since scroll bars affect size.
    var doc = isDoc(this.root) ? this.root : document;
    var html = doc.documentElement;
    var body = doc.body;
    rootRect = {
      top: 0,
      left: 0,
      right: html.clientWidth || body.clientWidth,
      width: html.clientWidth || body.clientWidth,
      bottom: html.clientHeight || body.clientHeight,
      height: html.clientHeight || body.clientHeight
    };
  }
  return this._expandRectByRootMargin(rootRect);
};


/**
 * Accepts a rect and expands it by the rootMargin value.
 * @param {DOMRect|ClientRect} rect The rect object to expand.
 * @return {ClientRect} The expanded rect.
 * @private
 */
IntersectionObserver.prototype._expandRectByRootMargin = function(rect) {
  var margins = this._rootMarginValues.map(function(margin, i) {
    return margin.unit == 'px' ? margin.value :
        margin.value * (i % 2 ? rect.width : rect.height) / 100;
  });
  var newRect = {
    top: rect.top - margins[0],
    right: rect.right + margins[1],
    bottom: rect.bottom + margins[2],
    left: rect.left - margins[3]
  };
  newRect.width = newRect.right - newRect.left;
  newRect.height = newRect.bottom - newRect.top;

  return newRect;
};


/**
 * Accepts an old and new entry and returns true if at least one of the
 * threshold values has been crossed.
 * @param {?IntersectionObserverEntry} oldEntry The previous entry for a
 *    particular target element or null if no previous entry exists.
 * @param {IntersectionObserverEntry} newEntry The current entry for a
 *    particular target element.
 * @return {boolean} Returns true if a any threshold has been crossed.
 * @private
 */
IntersectionObserver.prototype._hasCrossedThreshold =
    function(oldEntry, newEntry) {

  // To make comparing easier, an entry that has a ratio of 0
  // but does not actually intersect is given a value of -1
  var oldRatio = oldEntry && oldEntry.isIntersecting ?
      oldEntry.intersectionRatio || 0 : -1;
  var newRatio = newEntry.isIntersecting ?
      newEntry.intersectionRatio || 0 : -1;

  // Ignore unchanged ratios
  if (oldRatio === newRatio) return;

  for (var i = 0; i < this.thresholds.length; i++) {
    var threshold = this.thresholds[i];

    // Return true if an entry matches a threshold or if the new ratio
    // and the old ratio are on the opposite sides of a threshold.
    if (threshold == oldRatio || threshold == newRatio ||
        threshold < oldRatio !== threshold < newRatio) {
      return true;
    }
  }
};


/**
 * Returns whether or not the root element is an element and is in the DOM.
 * @return {boolean} True if the root element is an element and is in the DOM.
 * @private
 */
IntersectionObserver.prototype._rootIsInDom = function() {
  return !this.root || containsDeep(document, this.root);
};


/**
 * Returns whether or not the target element is a child of root.
 * @param {Element} target The target element to check.
 * @return {boolean} True if the target element is a child of root.
 * @private
 */
IntersectionObserver.prototype._rootContainsTarget = function(target) {
  var rootDoc =
    (this.root && (this.root.ownerDocument || this.root)) || document;
  return (
    containsDeep(rootDoc, target) &&
    (!this.root || rootDoc == target.ownerDocument)
  );
};


/**
 * Adds the instance to the global IntersectionObserver registry if it isn't
 * already present.
 * @private
 */
IntersectionObserver.prototype._registerInstance = function() {
  if (registry.indexOf(this) < 0) {
    registry.push(this);
  }
};


/**
 * Removes the instance from the global IntersectionObserver registry.
 * @private
 */
IntersectionObserver.prototype._unregisterInstance = function() {
  var index = registry.indexOf(this);
  if (index != -1) registry.splice(index, 1);
};


/**
 * Returns the result of the performance.now() method or null in browsers
 * that don't support the API.
 * @return {number} The elapsed time since the page was requested.
 */
function now() {
  return window.performance && performance.now && performance.now();
}


/**
 * Throttles a function and delays its execution, so it's only called at most
 * once within a given time period.
 * @param {Function} fn The function to throttle.
 * @param {number} timeout The amount of time that must pass before the
 *     function can be called again.
 * @return {Function} The throttled function.
 */
function throttle(fn, timeout) {
  var timer = null;
  return function () {
    if (!timer) {
      timer = setTimeout(function() {
        fn();
        timer = null;
      }, timeout);
    }
  };
}


/**
 * Adds an event handler to a DOM node ensuring cross-browser compatibility.
 * @param {Node} node The DOM node to add the event handler to.
 * @param {string} event The event name.
 * @param {Function} fn The event handler to add.
 * @param {boolean} opt_useCapture Optionally adds the even to the capture
 *     phase. Note: this only works in modern browsers.
 */
function addEvent(node, event, fn, opt_useCapture) {
  if (typeof node.addEventListener == 'function') {
    node.addEventListener(event, fn, opt_useCapture || false);
  }
  else if (typeof node.attachEvent == 'function') {
    node.attachEvent('on' + event, fn);
  }
}


/**
 * Removes a previously added event handler from a DOM node.
 * @param {Node} node The DOM node to remove the event handler from.
 * @param {string} event The event name.
 * @param {Function} fn The event handler to remove.
 * @param {boolean} opt_useCapture If the event handler was added with this
 *     flag set to true, it should be set to true here in order to remove it.
 */
function removeEvent(node, event, fn, opt_useCapture) {
  if (typeof node.removeEventListener == 'function') {
    node.removeEventListener(event, fn, opt_useCapture || false);
  }
  else if (typeof node.detachEvent == 'function') {
    node.detachEvent('on' + event, fn);
  }
}


/**
 * Returns the intersection between two rect objects.
 * @param {Object} rect1 The first rect.
 * @param {Object} rect2 The second rect.
 * @return {?Object|?ClientRect} The intersection rect or undefined if no
 *     intersection is found.
 */
function computeRectIntersection(rect1, rect2) {
  var top = Math.max(rect1.top, rect2.top);
  var bottom = Math.min(rect1.bottom, rect2.bottom);
  var left = Math.max(rect1.left, rect2.left);
  var right = Math.min(rect1.right, rect2.right);
  var width = right - left;
  var height = bottom - top;

  return (width >= 0 && height >= 0) && {
    top: top,
    bottom: bottom,
    left: left,
    right: right,
    width: width,
    height: height
  } || null;
}


/**
 * Shims the native getBoundingClientRect for compatibility with older IE.
 * @param {Element} el The element whose bounding rect to get.
 * @return {DOMRect|ClientRect} The (possibly shimmed) rect of the element.
 */
function getBoundingClientRect(el) {
  var rect;

  try {
    rect = el.getBoundingClientRect();
  } catch (err) {
    // Ignore Windows 7 IE11 "Unspecified error"
    // https://github.com/w3c/IntersectionObserver/pull/205
  }

  if (!rect) return getEmptyRect();

  // Older IE
  if (!(rect.width && rect.height)) {
    rect = {
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      left: rect.left,
      width: rect.right - rect.left,
      height: rect.bottom - rect.top
    };
  }
  return rect;
}


/**
 * Returns an empty rect object. An empty rect is returned when an element
 * is not in the DOM.
 * @return {ClientRect} The empty rect.
 */
function getEmptyRect() {
  return {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    width: 0,
    height: 0
  };
}


/**
 * Ensure that the result has all of the necessary fields of the DOMRect.
 * Specifically this ensures that `x` and `y` fields are set.
 *
 * @param {?DOMRect|?ClientRect} rect
 * @return {?DOMRect}
 */
function ensureDOMRect(rect) {
  // A `DOMRect` object has `x` and `y` fields.
  if (!rect || 'x' in rect) {
    return rect;
  }
  // A IE's `ClientRect` type does not have `x` and `y`. The same is the case
  // for internally calculated Rect objects. For the purposes of
  // `IntersectionObserver`, it's sufficient to simply mirror `left` and `top`
  // for these fields.
  return {
    top: rect.top,
    y: rect.top,
    bottom: rect.bottom,
    left: rect.left,
    x: rect.left,
    right: rect.right,
    width: rect.width,
    height: rect.height
  };
}


/**
 * Inverts the intersection and bounding rect from the parent (frame) BCR to
 * the local BCR space.
 * @param {DOMRect|ClientRect} parentBoundingRect The parent's bound client rect.
 * @param {DOMRect|ClientRect} parentIntersectionRect The parent's own intersection rect.
 * @return {ClientRect} The local root bounding rect for the parent's children.
 */
function convertFromParentRect(parentBoundingRect, parentIntersectionRect) {
  var top = parentIntersectionRect.top - parentBoundingRect.top;
  var left = parentIntersectionRect.left - parentBoundingRect.left;
  return {
    top: top,
    left: left,
    height: parentIntersectionRect.height,
    width: parentIntersectionRect.width,
    bottom: top + parentIntersectionRect.height,
    right: left + parentIntersectionRect.width
  };
}


/**
 * Checks to see if a parent element contains a child element (including inside
 * shadow DOM).
 * @param {Node} parent The parent element.
 * @param {Node} child The child element.
 * @return {boolean} True if the parent node contains the child node.
 */
function containsDeep(parent, child) {
  var node = child;
  while (node) {
    if (node == parent) return true;

    node = getParentNode(node);
  }
  return false;
}


/**
 * Gets the parent node of an element or its host element if the parent node
 * is a shadow root.
 * @param {Node} node The node whose parent to get.
 * @return {Node|null} The parent node or null if no parent exists.
 */
function getParentNode(node) {
  var parent = node.parentNode;

  if (node.nodeType == /* DOCUMENT */ 9 && node != document) {
    // If this node is a document node, look for the embedding frame.
    return getFrameElement(node);
  }

  // If the parent has element that is assigned through shadow root slot
  if (parent && parent.assignedSlot) {
    parent = parent.assignedSlot.parentNode;
  }

  if (parent && parent.nodeType == 11 && parent.host) {
    // If the parent is a shadow root, return the host element.
    return parent.host;
  }

  return parent;
}

/**
 * Returns true if `node` is a Document.
 * @param {!Node} node
 * @returns {boolean}
 */
function isDoc(node) {
  return node && node.nodeType === 9;
}


// Exposes the constructors globally.
window.IntersectionObserver = IntersectionObserver;
window.IntersectionObserverEntry = IntersectionObserverEntry;

}());

const init = (options = {}) => {
  const { featureFlags = {} } = options;
  Object.entries(featureFlags).forEach(([key, value]) => {
    featureFlagStore.set(key, value);
  });
};

const TestApi = /*#__PURE__*/Object.freeze({
  __proto__: null,
  init: init
});

const pendoCss = "body.light{--color-background:#ffffff;--color-background-secondary:#f1f6f8;--color-background-left-rail:#f4f9fa;--color-background-inverse:#182027;--color-background-gray:#cfd8dd;--color-background-disabled:#ecedee;--color-background-canvas:#cfd8dd;--color-background-highlight:#fee3dc;--color-background-raised:#ffffff;--color-background-edit:#ffffff;--color-background-gray-emp:#a4b1b8;--color-tile-background:#f1f6f8;--color-hover:#e9f1fa;--color-curtain:#000000;--color-border:#a4b1b8;--color-border-disabled:#cfd8dd;--color-border-grid:#f1f6f8;--color-border-de-emp:#cfd8dd;--color-selection:#da3b11;--color-logo:#fa4616;--color-foreground:#273139;--color-foreground-light:#6b7882;--color-foreground-emp:#182027;--color-foreground-de-emp:#526069;--color-foreground-disable:#8a97a0;--color-foreground-inverse:#f4f9fa;--color-foreground-highlight:#da3b11;--color-foreground-highlight-selected:rgba(218, 59, 17, 0.1);--color-foreground-highlight-selected-hover:rgba(218, 59, 17, 0.15);--color-foreground-link:#0067df;--color-foreground-link-disabled:#8a97a0;--color-foreground-link-pressed:#00489d;--color-foreground-inv-de-emp:#cfd8dd;--color-primary:#0067df;--color-primary-lighter:#badaff;--color-primary-darker:#00489d;--color-primary-hover:#0061d3;--color-primary-focused:#0061d3;--color-primary-pressed:#0067df;--color-secondary-focused:#e9f1fa;--color-secondary-pressed:#b2d2f3;--color-chip-default-background:#cfd8dd;--color-chip-error-background:#fff0f1;--color-chip-warning-background:#fff3db;--color-chip-info-background:#e9f1fa;--color-chip-success-background:#eeffe5;--color-error-icon:#cc3d45;--color-error-text:#a6040a;--color-error-background:#fff0f1;--color-error-foreground-inverse:#ff8484;--color-info-foreground:#1665b3;--color-info-icon:#1976d2;--color-info-text:#1665b3;--color-info-foreground-inverse:#42a1ff;--color-info-background:#e9f1fa;--color-success-icon:#6eb84a;--color-success-text:#038108;--color-success-background:#eeffe5;--color-success-foreground-inverse:#74c94b;--color-warning-icon:#ffb40e;--color-warning-text:#9e6100;--color-warning-background:#fff3db;--color-warning-foreground-inverse:#ffbb27;--color-icon-default:#526069;--color-icon-hover:#526069;--color-icon-inverted-default:#cfd8dd;--color-icon-inverted-hover:#f4f9fa;--color-toggle-off-hover:#f1f6f8;--color-toggle-off-pressed:#cfd8dd;--color-callout-background:#933692;--color-warn:#b6242b;--color-warn-darker:#a6040a;--color-warn-hover:rgba(182, 36, 43, 0.04);--color-warn-focus:rgba(182, 36, 43, 0.12);--color-warn-pressed:rgba(182, 36, 43, 0.08);--color-carousel-background:rgba(0, 103, 223, 0.04);--border:1px solid #a4b1b8;color-scheme:light}body.dark{--color-background:#182027;--color-background-secondary:#2d3d4d;--color-background-left-rail:#182027;--color-background-inverse:#f4f9fa;--color-background-gray:#526069;--color-background-disabled:#273139;--color-background-canvas:#526069;--color-background-highlight:#3c383c;--color-background-raised:#273139;--color-background-edit:rgba(0, 0, 0, 0.38);--color-background-gray-emp:#8a97a0;--color-tile-background:#374652;--color-hover:#2d3d4d;--color-curtain:#000000;--color-border:#8a97a0;--color-border-disabled:#526069;--color-border-grid:#273139;--color-border-de-emp:#526069;--color-selection:#ff8f70;--color-logo:#ffffff;--color-foreground:#f1f6f8;--color-foreground-light:#6b7882;--color-foreground-emp:#f4f9fa;--color-foreground-de-emp:#cfd8dd;--color-foreground-disable:#a4b1b8;--color-foreground-inverse:#182027;--color-foreground-highlight:#ff8f70;--color-foreground-highlight-selected:rgba(255, 143, 112, 0.1);--color-foreground-highlight-selected-hover:rgba(255, 143, 112, 0.15);--color-foreground-link:#66adff;--color-foreground-link-disabled:#8a97a0;--color-foreground-link-pressed:#badaff;--color-foreground-inv-de-emp:#526069;--color-primary:#66adff;--color-primary-lighter:#00489d;--color-primary-darker:#badaff;--color-primary-hover:#87bfff;--color-primary-focused:#87bfff;--color-primary-pressed:#66adff;--color-secondary-focused:#2d3d4d;--color-secondary-pressed:#11508d;--color-chip-default-background:#526069;--color-chip-error-background:#000000;--color-chip-warning-background:#000000;--color-chip-info-background:#000000;--color-chip-success-background:#000000;--color-error-icon:#ff8484;--color-error-text:#ff8484;--color-error-background:#000000;--color-error-foreground-inverse:#cc3d45;--color-info-foreground:#42a1ff;--color-info-icon:#42a1ff;--color-info-text:#42a1ff;--color-info-foreground-inverse:#1976d2;--color-info-background:#000000;--color-success-icon:#74c94b;--color-success-text:#74c94b;--color-success-background:#000000;--color-success-foreground-inverse:#6eb84a;--color-warning-icon:#ffbb27;--color-warning-text:#ffe19e;--color-warning-background:#000000;--color-warning-foreground-inverse:#ffb40e;--color-icon-default:#a4b1b8;--color-icon-hover:#cfd8dd;--color-icon-inverted-default:#526069;--color-icon-inverted-hover:#182027;--color-toggle-off-hover:#526069;--color-toggle-off-pressed:#8a97a0;--color-callout-background:#b748b6;--color-warn:#ff8484;--color-warn-darker:#ffadad;--color-warn-hover:rgba(255, 132, 132, 0.04);--color-warn-focus:rgba(255, 132, 132, 0.12);--color-warn-pressed:rgba(255, 132, 132, 0.08);--color-carousel-background:rgba(102, 173, 255, 0.04);--border:1px solid #8a97a0;color-scheme:dark}._pendo-guide-container_{background-color:var(--color-background-raised) !important;border-color:var(--color-border) !important;color:var(--color-foreground) !important;border-radius:3px !important}._pendo-guide-container_ ._pendo-guide-content_{color:var(--color-foreground) !important}._pendo-backdrop_{background-color:var(--color-curtain) !important;opacity:0.5 !important}._pendo-guide-arrow-top_{border-bottom-color:var(--color-background-raised) !important}._pendo-guide-arrow-left_{border-right-color:var(--color-background-raised) !important}._pendo-guide-arrow-right_{border-left-color:var(--color-background-raised) !important}._pendo-guide-arrow-bottom_{border-top-color:var(--color-background-raised) !important}._pendo-guide-arrow-border-top_{border-bottom-color:var(--color-background-raised) !important}._pendo-guide-arrow-border-left_{border-right-color:var(--color-background-raised) !important}._pendo-guide-arrow-border-right_{border-left-color:var(--color-background-raised) !important}._pendo-guide-arrow-border-bottom_{border-top-color:var(--color-background-raised) !important}._pendo-launcher_ ._pendo-launcher-header_{border-bottom-color:var(--color-border) !important}._pendo-launcher_ ._pendo-launcher-header_ ._pendo-launcher-title_{font-family:inherit;color:var(--color-foreground-de-emp) !important}._pendo-launcher_ ._pendo-launcher-header_ ._pendo-launcher-search-box_{border-color:var(--color-border) !important;background-color:var(--color-background-gray-emp) !important}._pendo-launcher_ ._pendo-launcher-footer_{color:var(--color-foreground-de-emp) !important}._pendo-guide-container_ ._pendo-poll-question_ ._pendo-poll-npsrating-choices_ label{color:var(--color-foreground-de-emp) !important;border-color:var(--color-border) !important}._pendo-guide-container_ ._pendo-poll-question_ ._pendo-poll-npsrating-choices_ label span{color:var(--color-foreground-de-emp) !important}._pendo-guide-container_ ._pendo-poll-npsreason_ textarea{border-color:var(--color-border) !important;width:100% !important}._pendo-guide-container_ ._pendo-poll-submit_{background-color:var(--color-primary) !important;color:var(--color-foreground-inverse) !important;font-family:inherit !important;border-color:var(--color-primary) !important;border-width:1px !important;border-style:solid !important;border-radius:3px !important}._pendo-poll_ ._pendo-poll-freeform_ textarea{color:var(--color-foreground) !important}._pendo-poll_ ._pendo-poll-npsrating_ ._pendo-poll-npsrating-choices_ label{color:var(--color-foreground-light) !important}._pendo-poll_ ._pendo-poll-npsrating_ ._pendo-poll-npsrating-choices_ label span{color:var(--color-foreground-light) !important}._pendo-poll_ ._pendo-poll-npsrating_ ._pendo-poll-npsrating-choices_ label:hover{border-color:var(--color-border) !important;background-color:var(--color-background-gray-emp) !important;color:var(--color-foreground-inverse) !important}._pendo-poll_ ._pendo-poll-npsrating_ ._pendo-poll-npsrating-choices_ ._pendo-poll-selected_{border-color:var(--color-foreground-de-emp) !important;background-color:var(--color-foreground-de-emp) !important;color:var(--color-foreground-inverse) !important}._pendo-poll_ ._pendo-poll-npsreason_{border-top-color:var(--color-border) !important}._pendo-poll_ ._pendo-poll-npsreason_ textarea{border-color:var(--color-border) !important}._pendo_row{background-color:transparent !important}.bb-text{color:var(--color-foreground) !important}._pendo-close-guide{color:var(--color-foreground-emp) !important;font-family:inherit !important;font-size:24px !important;height:24px !important;width:24px !important;border-radius:50% !important;top:10px !important;right:10px !important}._pendo-backdrop,.pendo-backdrop-region-right,.pendo-backdrop-region-top,.pendo-backdrop-region-bottom,.pendo-backdrop-region-left{background-color:var(--color-curtain) !important;opacity:0.5 !important}._pendo-close-guide:hover{background:var(--color-background-secondary) !important}._pendo-guide-container_{background-color:var(--color-background-raised) !important}._pendo-button-primaryButton{background-color:var(--color-primary) !important;color:var(--color-foreground-inverse) !important;font-family:inherit !important;border-color:var(--color-primary) !important;border-width:1px !important;border-style:solid !important}._pendo-button-primaryButton:hover{background-color:var(--color-primary-hover) !important}._pendo-button-primaryButton:focused{background-color:var(--color-primary-focused) !important}._pendo-button-secondaryButton{background-color:var(--color-background-raised) !important;color:var(--color-primary) !important;border-color:var(--color-border) !important;border-width:1px !important;border-style:solid !important}._pendo-button-secondaryButton:hover,._pendo-button-tertiaryButton:hover{background-color:var(--color-hover) !important}._pendo-button-tertiaryButton{color:var(--color-primary) !important;border-color:var(--color-background-raised) !important;border-width:1px !important;border-style:solid !important}._pendo-open-text-poll-input{background-color:var(--color-background) !important;color:inherit !important;border-radius:3px !important;font-family:inherit !important;width:100% !important}._pendo-nps-poll-wrapper input[type=radio]+label,._pendo-number-scale-poll-wrapper input[type=radio]+label{color:var(--color-primary) !important;border-color:var(--color-border) !important;border-width:1px !important;border-style:solid !important;font-family:inherit !important}._pendo-number-scale-poll-wrapper div{color:var(--color-foreground-disable) !important}._pendo-nps-poll-wrapper input[type=radio]:not(:checked)+label,._pendo-number-scale-poll-wrapper input[type=radio]:not(:checked)+label{background-color:var(--color-background-raised) !important}._pendo-nps-poll-wrapper input[type=radio]:hover+label,._pendo-number-scale-poll-wrapper input[type=radio]:hover+label{background-color:var(--color-hover) !important}._pendo-nps-poll-wrapper input[type=radio]:checked+label,._pendo-number-scale-poll-wrapper input[type=radio]:checked+label{background-color:var(--color-secondary-pressed) !important}._pendo-nps-poll-wrapper ._pendo-text-plain,._pendo-nps-poll-wrapper ._pendo-nps-poll-upperbound-label,._pendo-nps-poll-wrapper ._pendo-nps-poll-lowerbound-label{color:var(--color-foreground-de-emp) !important}._pendo-step-container-styles{box-shadow:0px 5px 10px rgba(0, 0, 0, 0.2), 0px 1px 5px rgba(0, 0, 0, 0.12), 0px 8px 10px rgba(0, 0, 0, 0.14) !important;border-color:var(--color-background-raised) !important;color:var(--color-foreground) !important;padding:24px !important;padding-top:20px !important;background-color:var(--color-background-raised) !important;border-radius:3px !important}._pendo-step-container-styles[data-caret-enabled=true]{background-color:#00489d !important;color:#ffffff !important;border-color:#00489d !important}._pendo-step-container-styles[data-caret-enabled=true] .bb-text{color:#ffffff !important;font-size:inherit !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-text-title,._pendo-step-container-styles[data-caret-enabled=true] ._pendo-text-subTitle{font-size:20px !important;line-height:24px !important;font-weight:600 !important;margin-bottom:6px !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-text-paragraph{line-height:21px !important;margin-right:6px !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-close-guide{color:#ffffff !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-close-guide:hover,._pendo-step-container-styles[data-caret-enabled=true] ._pendo-close-guide:focus{background-color:#2d3d4d !important;cursor:pointer !important}._pendo-step-container-styles[data-caret-enabled=true] .pendo-mock-flexbox-row{margin-top:24px !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button{font-weight:600 !important;margin-top:0px !important;margin-bottom:0px !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button-primaryButton{background-color:#ffffff !important;color:#0067df !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button-primaryButton:hover,._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button-primaryButton:focus{background-color:#e9f1fa !important;color:#0061d3 !important;font-weight:600 !important;border-color:#ffffff !important;cursor:pointer !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button-secondaryButton{background-color:transparent !important;color:#ffffff !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button-secondaryButton:hover,._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button-secondaryButton:focus{background-color:#00336f !important;color:#87bfff !important;font-weight:600 !important;border-color:#ffffff !important;cursor:pointer !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button-tertiaryButton{background-color:transparent !important;color:#ffffff !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button-tertiaryButton:hover,._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button-tertiaryButton:focus{background-color:#00336f !important;color:#87bfff !important;font-weight:600 !important;border-color:#ffffff !important;cursor:pointer !important}._pendo-step-container-styles[data-caret-enabled=true]+.pendo-tooltip-caret[style*=\"); top:\"],._pendo-step-container-styles[data-caret-enabled=true]~.pendo-tooltip-caret-border[style*=\"); top:\"]{border-bottom-color:#00489d !important}._pendo-step-container-styles[data-caret-enabled=true]+.pendo-tooltip-caret[style*=\"); right:\"],._pendo-step-container-styles[data-caret-enabled=true]~.pendo-tooltip-caret-border[style*=\"); right:\"]{border-left-color:#00489d !important}._pendo-step-container-styles[data-caret-enabled=true]+.pendo-tooltip-caret[style*=\"); bottom:\"],._pendo-step-container-styles[data-caret-enabled=true]~.pendo-tooltip-caret-border[style*=\"); bottom:\"]{border-top-color:#00489d !important}._pendo-step-container-styles[data-caret-enabled=true]+.pendo-tooltip-caret[style*=\"); left\"],._pendo-step-container-styles[data-caret-enabled=true]~.pendo-tooltip-caret-border[style*=\"); left:\"]{border-right-color:#00489d !important}._pendo-step-container-size[data-vertical-alignment=\"Bottom Aligned\"]{height:auto !important}._pendo-step-container-size[data-vertical-alignment=\"Bottom Aligned\"] ._pendo-step-container-styles{background:var(--color-background-raised) !important;box-shadow:0px -5px 10px rgba(0, 0, 0, 0.2), 0px 1px 5px rgba(0, 0, 0, 0.12), 0px 8px 10px rgba(0, 0, 0, 0.14) !important}._pendo-nps-open-text-poll-question{color:var(--color-foreground) !important}._pendo-text-title,._pendo-text-plain,._pendo-simple-text,._pendo-text-subTitle,._pendo-text-paragraph,._pendo-nps-poll-question,[id^=pendo-radio-select-],.pendo-radio,._pendo-text-paragraph._pendo-open-text-poll-question._pendo-nps-open-text-poll-question{color:inherit !important}._pendo-text-link{color:var(--color-primary) !important}._pendo-text-link:hover{text-decoration:underline !important;color:var(--color-primary-hover) !important}[id^=pendo-radio-select-] span{display:flex !important;align-items:center}[id^=pendo-radio-select-] .pendo-radio{color:var(--color-foreground) !important}[id^=pendo-radio-select-] input[type=radio]{background-color:var(--color-background-raised) !important;border:0.15em solid var(--color-foreground-de-emp) !important}[id^=pendo-radio-select-] input[type=radio]::before{content:\"\" !important;width:0.6em !important;height:0.6em !important;border-radius:50% !important;transform:scale(0) !important;transition:120ms transform ease-in-out !important;box-shadow:inset 1em 1em var(--color-primary) !important;background-color:var(--color-primary) !important}[id^=pendo-radio-select-] input[type=radio]:checked::before{transform:scale(1)}select._pendo-multi-choice-poll-select{color:inherit !important;background-color:transparent !important;border:none !important;padding:0 10px 0 0.5em !important;margin:0 !important;width:100% !important;font-family:inherit !important;line-height:inherit !important;border-radius:3px !important}._pendo-multi-choice-poll-select-border{background-color:inherit !important}._pendo-multi-choice-poll-select-border[data-_pendo-multi-choice-poll-select-border-1]{border:none !important}._pendo-multi-choice-poll-select-border[data-_pendo-multi-choice-poll-select-border-1]:hover{border-color:var(--color-foreground-de-emp) !important}._pendo-multi-choice-poll-select-border option._pendo-option{background-color:var(--color-background-raised) !important}._pendo_badge_{background-color:var(--color-background) !important}._pendo-badge-text{color:var(--color-primary) !important;font-size:14px !important;font-weight:600 !important}";

class Pendo {
  constructor(pendoApiKey = Pendo.getApiKey()) {
    this.isInitialized = false;
    // We cache the current visitor and account metadata so that we can detect changes and update the pendo instance when necessary
    // Updating too frequently can cause pendo guides to flash
    this.currentVisitorMetadata = {};
    this.currentAccountMetadata = {};
    this.apiKey = pendoApiKey;
    if (this.apiKey) {
      try {
        // @ts-ignore
        if (!window.pendo) {
          Pendo.mountScript(this.apiKey);
          Pendo.addOverrideStyles();
        }
        // @ts-ignore
        this.pendoInstance = window.pendo;
      }
      catch (e) {
        Logger.error('pendo', `Error mounting pendo script and/or styles: ${e}`);
      }
    }
    else {
      Logger.error('pendo', 'Missing api key');
    }
  }
  initializeFromStore(store) {
    this.initialize({
      visitor: Pendo.mapStoreToVisitorProps(store),
      account: Pendo.mapStoreToAccountProps(store),
    });
  }
  initialize(options) {
    if (!this.apiKey || !this.pendoInstance || this.isInitialized) {
      Logger.debug('pendo', `not initializing: key=${this.apiKey} pendo=${this.pendoInstance} initialized=${this.isInitialized}`);
      return;
    }
    try {
      const visitor = this.getVisitorOptions(options.visitor);
      const account = this.getAccountOptions(options.account);
      this.currentVisitorMetadata = visitor;
      this.currentAccountMetadata = account;
      Logger.debug('pendo', 'calling Pendo initialize');
      this.pendoInstance.initialize({
        visitor,
        account,
        excludeAllText: true,
        events: { ready: () => Pendo.onReadyCallback(this) },
      });
    }
    catch (exception) {
      Logger.error('pendo', 'Error initializing Pendo, exception: ' + exception, { stack: exception.stack });
    }
  }
  updateOptionsFromStore(store) {
    if (!this.isInitialized) {
      Logger.debug('pendo', 'not updating options, pendo DNE or not initialized');
      return;
    }
    Logger.debug('pendo', 'update pendo options from store');
    this.updateOptions({
      visitor: Pendo.mapStoreToVisitorProps(store),
      account: Pendo.mapStoreToAccountProps(store),
    });
  }
  updateOptions(options) {
    if (!this.isInitialized) {
      Logger.debug('pendo', 'not updating options, pendo DNE or not initialized');
      return;
    }
    // Get metadata and check if there are any changes
    let metadata = undefined;
    const visitor = this.getVisitorOptions(options.visitor);
    if (visitor) {
      metadata = { visitor };
      this.currentVisitorMetadata = Object.assign(Object.assign({}, this.currentVisitorMetadata), visitor);
    }
    const account = this.getAccountOptions(options.account);
    if (account) {
      metadata = Object.assign(Object.assign({}, metadata), { account });
      this.currentAccountMetadata = Object.assign(Object.assign({}, this.currentAccountMetadata), account);
    }
    // Only update if there are changes or the guides will flash
    if (metadata) {
      Logger.debug('pendo', 'update pendo options');
      this.pendoInstance.updateOptions(metadata);
    }
    else {
      Logger.debug('pendo', 'skipping; no change in options');
    }
  }
  updateVisitorOptions(visitorOptions) {
    if (!this.isInitialized) {
      Logger.debug('pendo', 'not updating options, pendo DNE or not initialized');
      return;
    }
    // Get metadata and only update if there are changes
    const visitor = this.getVisitorOptions(visitorOptions);
    if (visitor) {
      Logger.debug('pendo', 'update pendo visitor options', { object: visitorOptions });
      this.currentVisitorMetadata = Object.assign(Object.assign({}, this.currentVisitorMetadata), visitor);
      this.pendoInstance.updateOptions({ visitor });
    }
    else {
      Logger.debug('pendo', 'skipping; no change in visitor options');
    }
  }
  updateAccountOptions(accountOptions) {
    if (!this.isInitialized) {
      Logger.debug('pendo', 'not updating options, pendo DNE or not initialized');
      return;
    }
    // Get metadata and only update if there are changes
    const account = this.getAccountOptions(accountOptions);
    if (account) {
      Logger.debug('pendo', 'update pendo account options', { object: accountOptions });
      this.currentAccountMetadata = Object.assign(Object.assign({}, this.currentAccountMetadata), account);
      this.pendoInstance.updateOptions({ account });
    }
    else {
      Logger.debug('pendo', 'skipping; no change in account options');
    }
  }
  openGuide(guideId) {
    if (!this.isInitialized) {
      Logger.debug('pendo', 'not opening guide, pendo DNE or not initialized');
      return;
    }
    Logger.debug('pendo', `showing guide ${guideId}`);
    this.pendoInstance.showGuideById(guideId);
  }
  getCurrentMetadata() {
    return {
      visitor: this.currentVisitorMetadata,
      account: this.currentAccountMetadata,
    };
  }
  static getApiKey() {
    const spaceName = getSpaceName();
    const env = getEnvironment();
    if (env === 'alp' || env === 'stg' || env === 'prd') {
      return PendoApiKey[spaceName] || '';
    }
    return '';
  }
  static mountScript(pendoApiKey) {
    (function (p, e, n, d, o) {
      // @ts-ignore
      let v, w, x, y, z;
      o = p[d] = p[d] || {};
      o._q = [];
      // eslint-disable-next-line prefer-const
      v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
      for (w = 0, x = v.length; w < x; ++w) {
        (function (m) {
          // @ts-ignore
          o[m] = o[m] || function () {
            // @ts-ignore
            // eslint-disable-next-line prefer-rest-params
            o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0)));
          };
        })(v[w]);
      }
      // eslint-disable-next-line prefer-const
      y = e.createElement(n);
      y.async = !0;
      y.src = 'https://content.usage.uipath.com/agent/static/' + pendoApiKey + '/pendo.js';
      // eslint-disable-next-line prefer-const
      z = e.getElementsByTagName(n)[0];
      z.parentNode.insertBefore(y, z);
    })(window, document, 'script', 'pendo');
  }
  static addOverrideStyles() {
    Logger.debug('pendo', 'adding style element to header');
    const documentHead = document.head;
    const styleElement = document.createElement('style');
    styleElement.appendChild(document.createTextNode(pendoCss));
    documentHead.appendChild(styleElement);
  }
  static mapStoreToVisitorProps(store) {
    var _a, _b;
    const account = store.get('account');
    const urlTenantName = store.get('currentTenant');
    const activeTenantId = urlTenantName
      ? (_b = (_a = account === null || account === void 0 ? void 0 : account.tenants) === null || _a === void 0 ? void 0 : _a.find(tenant => { var _a; return ((_a = tenant.name) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === urlTenantName.toLowerCase(); })) === null || _b === void 0 ? void 0 : _b.id
      : undefined;
    return {
      userId: store.get('userId'),
      language: getLanguage(store) || 'en',
      activeTenantId,
      userGroups: account === null || account === void 0 ? void 0 : account.userGroups,
    };
  }
  static mapStoreToAccountProps(store) {
    var _a, _b, _c, _d;
    const account = store.get('account');
    const provisionedServices = [...new Set([
        ...((_b = (_a = account === null || account === void 0 ? void 0 : account.organization) === null || _a === void 0 ? void 0 : _a.accountServices) !== null && _b !== void 0 ? _b : []),
        ...((_d = (_c = account === null || account === void 0 ? void 0 : account.tenants) === null || _c === void 0 ? void 0 : _c.flatMap(tenant => tenant.enabledServices)) !== null && _d !== void 0 ? _d : []),
        'home',
        'help',
      ])];
    return {
      organizationId: store.get('organizationId'),
      accountType: account === null || account === void 0 ? void 0 : account.organization.accountType,
      provisionedServices,
    };
  }
  // The delegate has to be passed in or the instance's isInitialized field doesn't get set properly
  static onReadyCallback(delegate) {
    delegate.isInitialized = true;
    Logger.debug('pendo', 'ready, loading guides');
    delegate.pendoInstance.loadGuides();
    Logger.debug('pendo', `Pendo initialized successfully: ${delegate.isInitialized}`);
  }
  getVisitorOptions({ userId, language, activeTenantId, userGroups, customOptions, }) {
    Logger.debug('pendo', 'getting Pendo visitor options');
    const visitorOptions = Object.assign(Object.assign({}, customOptions), { id: userId, language: language === null || language === void 0 ? void 0 : language.replace('-', '_').toLowerCase(), language_uipath: language === null || language === void 0 ? void 0 : language.toLowerCase(), groups: userGroups, admin: userGroups === null || userGroups === void 0 ? void 0 : userGroups.includes(BuiltInGroups.Admin), activeTenantId });
    const filtered = removeObjectUndefinedProperties(visitorOptions);
    return this.hasNotUpdated(this.currentVisitorMetadata, filtered) ? undefined : filtered;
  }
  getAccountOptions({ organizationId, accountType, provisionedServices, customOptions, }) {
    Logger.debug('pendo', 'getting Pendo account options');
    const accountOptions = Object.assign(Object.assign({}, customOptions), { id: organizationId, accountType,
      provisionedServices });
    const filtered = removeObjectUndefinedProperties(accountOptions);
    return this.hasNotUpdated(this.currentAccountMetadata, filtered) ? undefined : filtered;
  }
  hasNotUpdated(options, subset) {
    const temp = Object.assign(Object.assign({}, options), subset);
    return JSON.stringify(temp) === JSON.stringify(options);
  }
}

// A set of global APIs that are meant to be consumed for tests (ie, not for consumers)
window['__unsafe__PORTAL_SHELL_TEST_UTILS__'] = TestApi;
// Instantiate singleton services
const pendo = new Pendo();
const featureFlags = new FeatureFlags(featureFlagStore);
// Set up initialize function
const initialize = (options) => {
  Logger.debug('globalScript', 'initialize', { object: options });
};
window.PortalShell = {
  initialize,
  Pendo: pendo,
  Logger: Logger,
  FeatureFlags: featureFlags,
};

//# sourceMappingURL=globalScript-7e029cd1.js.map